"use client";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Carousel } from '../structure/carousel';
import { SmartImage } from "../elements/smartimage";
import { ProductSchema } from '../foundation/schema';
import { addToShoppingCart } from "./shoppingcart.functions";
import { AddToCartButton, /* GoToCartButton */ ViewItemDetails } from "./shoppingcart.components";
import { getCloudinaryRemoteFetchURL as getImg } from "../integrations/cloudinary";
import { Loading, ToggleLoading } from "../foundation/loading";
import { usePixelatedConfig } from "../config/config.client";
import "../../css/pixelated.grid.scss";
import "./ebay.css";
import { getEbayAppToken, getEbayRateLimits, getEbayItems, getEbayItem, getEbayShoppingCartItem, getEbayProductSchema } from "./ebay.functions";
const debug = false;
/* ========== EBAY ITEMS PAGE ========== */
/**
 * EbayItems — Fetch and display eBay items using the configured API parameters and optional Cloudinary transforms.
 *
 * @param {object} [props.apiProps] - eBay API configuration and query parameters.
 * @param {string} [props.cloudinaryProductEnv] - Optional Cloudinary cloud name used to transform image URLs.
 */
EbayItems.propTypes = {
    /** eBay API configuration and query params */
    apiProps: PropTypes.object.isRequired,
    /** Optional Cloudinary product environment for image transforms */
    cloudinaryProductEnv: PropTypes.string,
};
export function EbayItems(props) {
    // https://developer.ebay.com/devzone/finding/HowTo/GettingStarted_JS_NV_JSON/GettingStarted_JS_NV_JSON.html
    const config = usePixelatedConfig();
    const [items, setItems] = useState([]);
    const [aspects, setAspects] = useState([]);
    const apiProps = { ...(config?.integrations?.ebay || {}), ...props.apiProps };
    /**
    * paintItems — Map raw eBay item data into rendered `EbayListItem` elements.
    *
    * @param {array} [props.items] - Array of eBay item objects returned by the API.
    * @param {string} [props.cloudinaryProductEnv] - Optional Cloudinary cloud name for image URL transformations.
    */
    paintItems.propTypes = {
        /** Array of eBay item objects */
        items: PropTypes.array.isRequired,
        /** Optional Cloudinary product environment */
        cloudinaryProductEnv: PropTypes.string,
    };
    function paintItems(props) {
        if (debug)
            console.log("Painting Items");
        let newItems = [];
        for (let key in props.items) {
            const item = props.items[key];
            const newItem = _jsx(EbayListItem, { item: item, apiProps: apiProps, cloudinaryProductEnv: props.cloudinaryProductEnv }, item.legacyItemId);
            newItems.push(newItem);
        }
        return newItems;
    }
    /**
    * fetchItems — Perform a search query against eBay and update component state with results.
    *
    * @param {string} [props.aspectName] - Optional aspect name to filter search results.
    * @param {string} [props.aspectValue] - Optional aspect value corresponding to `aspectName` to filter results.
    */
    fetchItems.propTypes = {
        /** Filter aspect name for the search (optional) */
        aspectName: PropTypes.string,
        /** Filter aspect value for the search (optional) */
        aspectValue: PropTypes.string,
    };
    async function fetchItems(props) {
        try {
            if (debug)
                console.log("Fetching ebay API Items Data");
            const myApiProps = { ...apiProps };
            if (props) {
                const params = new URLSearchParams(myApiProps.qsSearchURL);
                let aspects = params.get('aspect_filter');
                const newAspects = props.aspectName + ":{" + props.aspectValue + "}";
                aspects = (aspects) ? aspects + "," + newAspects : newAspects;
                params.set('aspect_filter', aspects);
                myApiProps.qsSearchURL = "?" + decodeURIComponent(params.toString());
            }
            const response = await getEbayItems({ apiProps: myApiProps });
            if (debug)
                console.log("eBay API Search Items Data:", response);
            setItems(response?.itemSummaries);
            setAspects(response?.refinement?.aspectDistributions);
        }
        catch (error) {
            console.error("Error fetching eBay items:", error);
        }
    }
    useEffect(() => {
        if (debug)
            console.log("Running useEffect");
        (async () => {
            ToggleLoading(true);
            await fetchItems();
            ToggleLoading(false);
        })();
    }, []);
    if (items && items.length > 0) {
        return (_jsxs(_Fragment, { children: [_jsx(Loading, {}), _jsx("div", { className: "ebay-items-header", children: _jsx(EbayItemHeader, { title: `${items.length} Store Items` }) }), _jsx("div", { className: "ebay-items-header", children: _jsx(EbayListFilter, { aspects: aspects, callback: fetchItems }) }), _jsxs("div", { id: "ebay-items", className: "ebay-items", children: [items.map((item) => {
                            const productSchema = getEbayProductSchema({
                                item,
                                brandName: 'eBay',
                                siteUrl: item.itemWebUrl,
                                siteInfo: config?.siteInfo,
                                routes: config?.routes,
                            });
                            return productSchema ? _jsx(ProductSchema, { product: productSchema }, `ebay-schema-${item.legacyItemId}`) : null;
                        }), paintItems({ items: items, cloudinaryProductEnv: props.cloudinaryProductEnv })] })] }));
    }
    else {
        return (_jsx("div", { className: "section-container", children: _jsx("div", { id: "ebay-items", className: "ebay-items", children: _jsx(Loading, {}) }) }));
    }
}
/**
 * EbayListFilter — UI for filtering eBay item lists by aspect values.
 *
 * @param {any} [props.aspects] - Aspect distributions returned by eBay used to build filter controls.
 * @param {function} [props.callback] - Callback invoked when a filter selection changes; receives filter criteria.
 */
EbayListFilter.propTypes = {
    /** Aspect distributions used to render filter controls */
    aspects: PropTypes.any.isRequired,
    /** Callback to fetch filtered results */
    callback: PropTypes.func.isRequired,
};
export function EbayListFilter(props) {
    const [selectedAspectName, setSelectedAspectName] = useState('');
    const [selectedAspectValue, setSelectedAspectValue] = useState('');
    if (!props.aspects || !Array.isArray(props.aspects)) {
        return null;
    }
    const aspectNames = props.aspects.map((aspect) => (aspect.localizedAspectName)).sort();
    const aspectNamesValues = {};
    for (const aspect of props.aspects) {
        const thisAspectName = aspect.localizedAspectName;
        const aspectNameValues = aspect.aspectValueDistributions.map((aspectValue) => {
            return (aspectValue.localizedAspectValue);
        }).sort();
        aspectNamesValues[thisAspectName] = aspectNameValues;
    }
    const selectedAspectValues = selectedAspectName ? (aspectNamesValues[selectedAspectName] || []) : [];
    function onAspectNameChange(event) {
        setSelectedAspectName(event.target.value);
        setSelectedAspectValue('');
    }
    function onAspectValueChange(event) {
        setSelectedAspectValue(event.target.value);
    }
    function handleAspectFilter() {
        if (selectedAspectName && selectedAspectValue) {
            props.callback({ aspectName: selectedAspectName, aspectValue: selectedAspectValue });
        }
    }
    return (_jsxs("form", { name: "ebay-items-filter", id: "ebay-items-filter", children: [_jsxs("span", { className: "filter-input", children: [_jsx("label", { htmlFor: "aspectName", children: "Aspect:" }), _jsxs("select", { id: "aspectName", value: selectedAspectName, onChange: onAspectNameChange, children: [_jsx("option", { value: "" }), aspectNames.map((aspectName, index) => _jsx("option", { value: aspectName, children: aspectName }, index))] })] }), _jsxs("span", { className: "filter-input", children: [_jsx("label", { htmlFor: "aspectValue", children: "Value:" }), _jsxs("select", { id: "aspectValue", value: selectedAspectValue, onChange: onAspectValueChange, disabled: !selectedAspectName, children: [_jsx("option", { value: "" }), selectedAspectValues.map((aspectValueString, index) => (_jsx("option", { value: aspectValueString, children: aspectValueString }, index)))] })] }), _jsx("span", { className: "filter-input", children: _jsx("button", { type: "button", onClick: handleAspectFilter, disabled: !selectedAspectName || !selectedAspectValue, children: "Filter" }) })] }));
}
/**
 * EbayListItem — Render a single eBay item with thumbnail, metadata and add-to-cart actions.
 *
 * @param {any} [props.item] - eBay item object returned by the API.
 * @param {string} [props.cloudinaryProductEnv] - Optional Cloudinary cloud name to transform image URLs.
 * @param {any} [props.apiProps] - eBay API properties (for link generation or calls).
 */
EbayListItem.propTypes = {
    /** eBay item object */
    item: PropTypes.any.isRequired,
    /** Optional Cloudinary product environment */
    cloudinaryProductEnv: PropTypes.string,
    /** eBay API properties */
    apiProps: PropTypes.any,
};
export function EbayListItem(props) {
    const thisItem = props.item;
    const apiProps = props.apiProps;
    // const itemURL = thisItem.itemWebUrl;
    const itemURL = "./store/" + thisItem.legacyItemId;
    const itemURLTarget = "_self"; /* "_blank" */
    const itemImage = (props.cloudinaryProductEnv)
        ? getImg({ url: thisItem.thumbnailImages?.[0]?.imageUrl || thisItem.image?.imageUrl || '', product_env: props.cloudinaryProductEnv })
        : thisItem.thumbnailImages?.[0]?.imageUrl || thisItem.image?.imageUrl || '';
    const shoppingCartItem = getEbayShoppingCartItem({ thisItem: thisItem, cloudinaryProductEnv: props.cloudinaryProductEnv, apiProps: apiProps });
    // CHANGE EBAY URL TO LOCAL EBAY ITEM DETAIL URL
    shoppingCartItem.itemURL = itemURL;
    const config = usePixelatedConfig();
    const itemImageComponent = _jsx(SmartImage, { src: itemImage, title: thisItem.title, alt: thisItem.title, cloudinaryEnv: props.cloudinaryProductEnv ?? undefined, cloudinaryDomain: config?.integrations?.cloudinary?.baseUrl ?? undefined, cloudinaryTransforms: config?.integrations?.cloudinary?.transforms ?? undefined });
    return (_jsxs("div", { className: "ebay-item row-12col", children: [_jsx("div", { className: "ebay-item-photo grid-s1-e5", children: itemURL
                    ? _jsx("a", { href: itemURL, target: itemURLTarget, rel: "noopener noreferrer", children: itemImageComponent })
                    : (itemImageComponent) }), _jsxs("div", { className: "ebay-item-body grid-s5-e13", children: [_jsx("div", { className: "ebay-item-header", children: itemURL
                            ? _jsx(EbayItemHeader, { url: itemURL, target: itemURLTarget, title: thisItem.title })
                            : _jsx(EbayItemHeader, { title: thisItem.title }) }), _jsxs("div", { className: "ebay-item-details grid12", children: [_jsxs("div", { children: [_jsx("b", { children: "Item ID: " }), thisItem.legacyItemId] }), _jsxs("div", { children: [_jsx("b", { children: "Quantity: " }), thisItem.categories?.[0]?.categoryId == apiProps.itemCategory ? 1 : 10] }), _jsxs("div", { children: [_jsx("b", { children: "Condition: " }), thisItem.condition] }), _jsxs("div", { children: [_jsx("b", { children: "Seller: " }), thisItem.seller?.username, " (", thisItem.seller?.feedbackScore, ")", _jsx("br", {}), thisItem.seller?.feedbackPercentage, "% positive"] }), _jsxs("div", { children: [_jsx("b", { children: "Buying Options: " }), thisItem.buyingOptions?.[0]] }), _jsxs("div", { children: [_jsx("b", { children: "Location: " }), thisItem.itemLocation?.postalCode + ", " + thisItem.itemLocation?.country] }), _jsxs("div", { children: [_jsx("b", { children: "Listing Date: " }), thisItem.itemCreationDate] })] }), _jsx("div", { className: "ebay-item-price", children: itemURL
                            ? _jsxs("a", { href: itemURL, target: itemURLTarget, rel: "noreferrer", children: ["$", thisItem.price.value + " " + thisItem.price.currency] })
                            : "$" + thisItem.price.value + " " + thisItem.price.currency }), _jsx("br", {}), _jsxs("div", { className: "ebay-item-add-to-cart", children: [_jsx(ViewItemDetails, { href: "/store", itemID: thisItem.legacyItemId }), _jsx(AddToCartButton, { handler: addToShoppingCart, item: shoppingCartItem, itemID: thisItem.legacyItemId })] })] })] }));
}
/**
 * EbayItemHeader — Render a heading for an eBay item; optionally wrap in a link when a `url` is provided.
 *
 * @param {string} [props.title] - The item title to display.
 * @param {string} [props.url] - Optional URL to link the title to (opens in `props.target`).
 * @param {string} [props.target] - Link target attribute (e.g., '_blank').
 */
EbayItemHeader.propTypes = {
    /** The item title text */
    title: PropTypes.string.isRequired,
    /** Optional link URL for the title */
    url: PropTypes.string,
    /** Link target attribute (e.g., '_blank') */
    target: PropTypes.string,
};
export function EbayItemHeader(props) {
    return (_jsx("span", { children: props.url
            ? _jsx("a", { href: props.url, target: props.target ?? '', rel: "noopener noreferrer", children: _jsx("h2", { className: "", children: props.title }) })
            : _jsx("h2", { className: "", children: props.title }) }));
}
/* ========== EBAY ITEM DETAIL PAGE ========== */
/**
 * EbayItemDetail — Display detailed information for a single eBay item fetched via the API.
 *
 * @param {object} [props.apiProps] - eBay API configuration used to fetch the item.
 * @param {string} [props.itemID] - eBay item identifier to fetch details for.
 * @param {string} [props.cloudinaryProductEnv] - Optional Cloudinary product environment for image transforms.
 */
EbayItemDetail.propTypes = {
    /** eBay API configuration */
    apiProps: PropTypes.object.isRequired,
    /** eBay item ID to fetch details for */
    itemID: PropTypes.string.isRequired, // currently not used
    /** Cloudinary product env for image transforms */
    cloudinaryProductEnv: PropTypes.string,
};
export function EbayItemDetail(props) {
    const config = usePixelatedConfig();
    const [item, setItem] = useState({});
    const apiProps = { ...(config?.integrations?.ebay || {}), ...props.apiProps };
    useEffect(() => {
        if (debug)
            console.log("Running useEffect");
        (async () => {
            try {
                const response = await getEbayItem({ apiProps: apiProps });
                if (debug)
                    console.log("eBay API Get Items Data", response);
                setItem(response);
            }
            catch (error) {
                console.error("Error fetching eBay items:", error);
            }
        })();
    }, []);
    if (item && Object.keys(item) && Object.keys(item).length > 0) {
        const thisItem = { ...item };
        if (debug)
            console.log(thisItem);
        const images = (thisItem.additionalImages || []).map((thisImage) => ({
            image: (props.cloudinaryProductEnv)
                ? getImg({ url: thisImage.imageUrl, product_env: props.cloudinaryProductEnv })
                : thisImage.imageUrl
        }));
        const itemURL = undefined;
        const itemURLTarget = "_self"; /* "_blank" */
        const shoppingCartItem = getEbayShoppingCartItem({ thisItem: thisItem, cloudinaryProductEnv: props.cloudinaryProductEnv, apiProps: apiProps });
        shoppingCartItem.itemURL = itemURL;
        const productSchema = getEbayProductSchema({
            item: thisItem,
            brandName: 'eBay',
            siteUrl: thisItem.itemWebUrl,
            siteInfo: config?.siteInfo,
            routes: config?.routes,
        });
        return (_jsxs(_Fragment, { children: [productSchema ? _jsx(ProductSchema, { product: productSchema }) : null, _jsxs("div", { className: "ebay-item row-12col", children: [_jsx("div", { className: "ebay-item-header grid-s1-e13", children: itemURL
                                ? _jsx(EbayItemHeader, { url: itemURL, title: thisItem.title })
                                : _jsx(EbayItemHeader, { title: thisItem.title }) }), _jsx("br", {}), _jsx("div", { className: "ebay-item-photo-carousel grid-s1-e7", children: _jsx(Carousel, { cards: images, draggable: true, imgFit: "contain" }) }), _jsxs("div", { className: "grid-s7-e13", children: [_jsx("div", { className: "ebay-item-details grid12", children: _jsx("div", { dangerouslySetInnerHTML: { __html: thisItem.description?.replace(/(<br\s*\/?>\s*){2,}/gi, '') || '' } }) }), _jsx("br", {}), _jsxs("div", { className: "ebay-item-details grid12", children: [_jsxs("div", { children: [_jsx("b", { children: "Item ID: " }), thisItem.legacyItemId] }), _jsxs("div", { children: [_jsx("b", { children: "Quantity: " }), thisItem.categoryId == apiProps.itemCategory ? 1 : 10] }), _jsxs("div", { children: [_jsx("b", { children: "Category: " }), thisItem.categoryPath] }), _jsxs("div", { children: [_jsx("b", { children: "Condition: " }), thisItem.condition] }), _jsxs("div", { children: [_jsx("b", { children: "Seller: " }), thisItem.seller?.username, " (", thisItem.seller?.feedbackScore, ")", _jsx("br", {}), thisItem.seller?.feedbackPercentage, "% positive"] }), _jsxs("div", { children: [_jsx("b", { children: "Buying Options: " }), thisItem.buyingOptions?.[0]] }), _jsxs("div", { children: [_jsx("b", { children: "Location: " }), thisItem.itemLocation?.city + ", " + thisItem.itemLocation?.stateOrProvince] }), _jsxs("div", { children: [_jsx("b", { children: "Listing Date: " }), thisItem.itemCreationDate] }), _jsx("br", {})] }), _jsx("div", { className: "ebay-item-price", children: itemURL
                                        ? _jsxs("a", { href: itemURL, target: itemURLTarget, rel: "noreferrer", children: ["$", thisItem.price.value + " " + thisItem.price.currency] })
                                        : "$" + thisItem.price.value + " " + thisItem.price.currency }), _jsx("br", {}), _jsx("div", { className: "ebay-item-add-to-cart", children: _jsx(AddToCartButton, { handler: addToShoppingCart, item: shoppingCartItem, itemID: thisItem.legacyItemId }) })] })] })] }));
    }
    else {
        return (_jsx(_Fragment, { children: _jsx("div", { id: "ebay-items", className: "ebay-items", children: _jsx("div", { className: "centered", children: "Loading..." }) }) }));
    }
}
/* ========== EBAY RATE LIMITS VISUALIZER ========== */
/**
 * EbayRateLimitsVisualizer — Visualize eBay API rate limit information for debugging and monitoring.
 *
 * @param {string} [props.token] - OAuth access token used to authorize analytics requests.
 * @param {object} [props.apiProps] - eBay analytics API configuration (baseAnalyticsURL, proxyURL).
 */
EbayRateLimitsVisualizer.propTypes = {
    /** OAuth access token for analytics endpoints */
    token: PropTypes.string,
    /** eBay analytics API configuration */
    apiProps: PropTypes.object,
};
export function EbayRateLimitsVisualizer(props) {
    const config = usePixelatedConfig();
    const apiProps = {
        ...(config?.integrations?.ebay || {}),
        ...props.apiProps
    };
    const [token, setToken] = useState(props.token || '');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fetchingToken, setFetchingToken] = useState(false);
    const [error, setError] = useState(null);
    const fetchToken = async () => {
        setFetchingToken(true);
        setError(null);
        try {
            const newToken = await getEbayAppToken({ apiProps });
            if (newToken) {
                setToken(newToken);
                return newToken;
            }
            else {
                setError('Failed to fetch eBay token. Check your appId and appCertId.');
            }
        }
        catch (e) {
            setError('Token fetch error: ' + e.message);
        }
        finally {
            setFetchingToken(false);
        }
    };
    // Auto-fetch token on mount if credentials are available
    useEffect(() => {
        if (!token && apiProps.appId && apiProps.appCertId) {
            fetchToken();
        }
    }, []);
    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getEbayRateLimits({
                token: token,
                apiProps: apiProps
            });
            setData(result);
        }
        catch (e) {
            setError(e.message || 'Failed to fetch data');
        }
        finally {
            setLoading(false);
        }
    };
    const showMockData = () => {
        setData({
            rate_limit: {
                apiContext: "Buy",
                apiName: "Browse",
                apiVersion: "v1",
                resources: [
                    {
                        resourceName: "item_summary",
                        methods: [
                            {
                                methodName: "search",
                                quotaTotal: 5000,
                                quotaRemaining: 4950,
                                quotaResets: "2026-01-10T00:00:00.000Z"
                            }
                        ]
                    }
                ]
            },
            user_rate_limit: {
                userContext: "Individual",
                resources: [
                    {
                        resourceName: "item",
                        methods: [
                            {
                                methodName: "get",
                                quotaTotal: 1000,
                                quotaRemaining: 990,
                                quotaResets: "2026-01-10T00:00:00.000Z"
                            }
                        ]
                    }
                ]
            }
        });
    };
    // Check for API-level errors in the returned data
    const hasTokenError = data?.rate_limit?.errors?.[0]?.message === "Invalid access token" ||
        data?.user_rate_limit?.errors?.[0]?.message === "Invalid access token";
    return (_jsxs("div", { style: { padding: '20px', fontFamily: 'sans-serif', border: '1px solid #ddd', borderRadius: '8px' }, children: [_jsx("h3", { children: "Ebay Rate Limits Data Visualizer" }), _jsxs("div", { style: { marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }, children: [_jsxs("div", { style: { display: 'flex', gap: '10px', alignItems: 'center' }, children: [_jsx("label", { htmlFor: "ebay-token", children: _jsx("b", { children: "Token:" }) }), _jsx("input", { id: "ebay-token", type: "text", value: token, onChange: (e) => setToken(e.target.value), placeholder: "Paste eBay token here", style: { flexGrow: 1, padding: '5px', fontFamily: 'monospace' } }), _jsx("button", { onClick: fetchToken, disabled: fetchingToken || !apiProps.appId, children: fetchingToken ? 'Fetching Token...' : 'Auto-Fetch Token' })] }), _jsxs("div", { style: { display: 'flex', gap: '10px' }, children: [_jsx("button", { onClick: fetchData, disabled: loading || !token, style: { padding: '8px 16px', cursor: 'pointer', background: '#0070f3', color: 'white', border: 'none', borderRadius: '4px' }, children: loading ? 'Fetching Limits...' : 'Fetch Rate Limits' }), _jsx("button", { onClick: showMockData, style: { padding: '8px 16px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '4px', background: 'white' }, children: "Load Sample Structure" })] })] }), error && (_jsxs("div", { style: { color: '#d00', background: '#fff5f5', padding: '10px', borderRadius: '4px', marginBottom: '10px', border: '1px solid #feb2b2' }, children: [_jsx("b", { children: "Error:" }), " ", error] })), hasTokenError && (_jsxs("div", { style: { color: '#c53030', background: '#fff5f5', padding: '10px', borderRadius: '4px', marginBottom: '10px', border: '1px solid #feb2b2' }, children: [_jsx("span", { role: "img", "aria-label": "error", children: "\uD83D\uDEA8" }), " ", _jsx("b", { children: "Authentication Error:" }), " Your eBay access token is invalid or expired. Use \"Auto-Fetch Token\" if credentials are set, or paste a new one."] })), data ? (_jsxs("div", { style: { marginTop: '20px' }, children: [_jsxs("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' }, children: [_jsx("h4", { children: "Response Data:" }), _jsx("button", { onClick: () => setData(null), style: { padding: '4px 8px', fontSize: '12px' }, children: "Clear" })] }), _jsx("pre", { style: { background: '#f8f9fa', padding: '15px', borderRadius: '5px', overflow: 'auto', border: '1px solid #e9ecef', fontSize: '13px' }, children: JSON.stringify(data, null, 2) })] })) : (_jsx("div", { style: { color: '#666', fontStyle: 'italic', marginTop: '20px' }, children: "No rate limit data loaded yet." }))] }));
}
